'use client';
import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { DialogTrigger } from '@radix-ui/react-dialog';
import ErrorIcon from '@/components/customercenter-ui/svgs/notifications/error';
import SuccessIcon from '@/components/customercenter-ui/svgs/notifications/success';
import { useAccount } from '@/frontastic';
import { ErrorNotification, SuccessNotification } from '@/helpers/utils/notification';
import { formatDateMoment, isCreditSite, TranslateText } from '@/helpers/utils/siteUtils';
import {
  CustomerPreferencePayload,
  Preferences,
  updateCustomerPreferencesPayload,
} from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import {
  DATE_FORMAT,
  DEFAULT_SITE_LANG,
  DIVISION_LEVEL,
  MASTER_LEVEL,
  TIME_FORMAT,
} from '../../../../../constants/constant';
import {
  CUSTOMER_PREFRENCE_HEADER_KEYS,
  CUSTOMER_PREFRENCE_RECORD_KEYS,
  PAYLOAD_QUERY,
  RESPONSE_SUCCESS_MESSAGE,
} from '../../customer-detail/constants';
import { PageTexts } from '../../customer-detail/i18n';
import { Preference } from '../../customer-detail/types';
import { segregatePreferences } from '../../customer-detail/utils';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/customSelect/customSelect';
import { Table, TableColumnConfig } from '../../ui/customTable/Table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import Loader from '../../ui/loader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { useToast } from '../../ui/use-toast';

const CustomerPrefrenceModal = () => {
  const { toast } = useToast();
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const params = useSearchParams();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { getCustomerPreferences, updateCustomerPreferences } = useAccount();
  const [currentEditType, setCurrentEditType] = useState(DIVISION_LEVEL);
  const [initialPreferenceData, setInitialPreferenceData] = useState({
    masterLevel: [] as Preference[],
    divisionLevel: [] as Preference[],
  });
  const [masterPreferenceData, setMasterPreferenceData] = useState<Preference[]>();
  const [divisionPreferenceData, setDivisionPreferenceData] = useState<Preference[]>();

  const getCustomerPrefData = async () => {
    setLoading(true);
    const payload = {
      customerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID),
      divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
    } as CustomerPreferencePayload;
    const response = await getCustomerPreferences(payload)
      .then((res) => res)
      .catch(() => setLoading(false));
    setLoading(false);
    const { masterLevelPreferences, divisionLevelPreferences } = segregatePreferences(response?.Preferences || []);
    let divisionLevelData = divisionLevelPreferences;
    if (!isCreditSite()) {
      divisionLevelData = divisionLevelData.filter((preferencesData) => preferencesData.name != 'DIV-DBPRT-PREF');
    }
    setInitialPreferenceData({ masterLevel: masterLevelPreferences, divisionLevel: divisionLevelData });
    setMasterPreferenceData(masterLevelPreferences);
    setDivisionPreferenceData(divisionLevelData);
  };

  const TABLE_HEADER: TableColumnConfig<any>[] = [
    {
      key: CUSTOMER_PREFRENCE_HEADER_KEYS.displayName,
      header: CUSTOMER_PREFRENCE_RECORD_KEYS.description,
    },
    {
      key: CUSTOMER_PREFRENCE_HEADER_KEYS.value,
      header: CUSTOMER_PREFRENCE_RECORD_KEYS.value,
      render: (data, rowData) => (
        <Select
          defaultValue={rowData.value ? `${Texts.selectYesValue}` : `${Texts.selectNoValue}`}
          onValueChange={(value) => {
            const selectValue = value === Texts.selectYesValue;
            updatePreferenceData(rowData.displayName, selectValue, rowData.updatedBy, rowData.updatedOn);
          }}
        >
          <SelectTrigger className="mr-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white px-4">
            <SelectItem value={Texts.selectYesValue}>{Texts.selectYesValue}</SelectItem>
            <SelectItem value={Texts.selectNoValue}>{Texts.selectNoValue}</SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      key: CUSTOMER_PREFRENCE_HEADER_KEYS.updatedBy,
      header: CUSTOMER_PREFRENCE_RECORD_KEYS.agent,
      render: (data) => <div className="gbmask">{data ?? ''}</div>,
    },
    {
      key: CUSTOMER_PREFRENCE_HEADER_KEYS.updatedOn,
      header: CUSTOMER_PREFRENCE_RECORD_KEYS.timeStamp,
      render: (data) => (
        <div>
          <div>{formatDateMoment(data, DATE_FORMAT)}</div>
          <div>{formatDateMoment(data, TIME_FORMAT)}</div>
        </div>
      ),
    },
  ];

  const OnCancel = (reset: boolean) => {
    if (reset) {
      setDivisionPreferenceData(initialPreferenceData.divisionLevel);
      setMasterPreferenceData(initialPreferenceData.masterLevel);
    }
  };

  const updatePreferenceData = (description: string, value: boolean, updatedBy: string, updatedOn: string) => {
    if (currentEditType === MASTER_LEVEL) {
      const updatedData = masterPreferenceData?.map((pref) => {
        if (pref.displayName === description) {
          return {
            ...pref,
            value,
            updatedBy,
            updatedOn,
          };
        }
        return pref;
      });
      setMasterPreferenceData(updatedData);
    } else {
      const updatedData = divisionPreferenceData?.map((pref) => {
        if (pref?.displayName === description) {
          return {
            ...pref,
            value,
            updatedBy,
            updatedOn,
          };
        }
        return pref;
      });

      setDivisionPreferenceData(updatedData);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    const updatedMasterPreferences = masterPreferenceData?.filter((pref, index) => {
      const initialPref = initialPreferenceData.masterLevel[index];
      return initialPref.value !== pref.value;
    });
    const updatedDivisionPreferences = divisionPreferenceData?.filter((pref, index) => {
      const initialPref = initialPreferenceData.divisionLevel[index];
      return (
        initialPref.value !== pref.value ||
        initialPref.updatedBy !== pref.updatedBy ||
        initialPref.updatedOn !== pref.updatedOn
      );
    });
    const Preferences =
      currentEditType === MASTER_LEVEL
        ? updatedMasterPreferences?.map(({ name, value }) => ({ name, value })) || ([] as Preferences[])
        : updatedDivisionPreferences?.map(({ name, value }) => ({ name, value })) || ([] as Preferences[]);

    const payload = {
      query: {
        customerId: params.get(PAYLOAD_QUERY.CUSTOMER_ID),
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
      },
      data: { preferences: Preferences },
    } as updateCustomerPreferencesPayload;
    if (Preferences?.length > 0) {
      await updateCustomerPreferences(payload).then((response) => {
        setLoading(false);
        if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
          setOpen(false);
          setCurrentEditType(DIVISION_LEVEL);
          SuccessNotification(toast, { description: response?.message, action: <SuccessIcon className="h-5 w-5" /> });
        } else {
          ErrorNotification(toast, {
            description: Texts.errorUpdatingCustomerPreferences,
            action: <ErrorIcon className="h-5 w-5" />,
          });
        }
      });
    } else {
      ErrorNotification(toast, {
        description: Texts.errorEditCustomerPreferences,
        action: <ErrorIcon className="h-5 w-5" />,
      });
      setLoading(false);
    }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setCurrentEditType(DIVISION_LEVEL);
        setOpen(value);
        OnCancel(!value);
      }}
    >
      <DialogTrigger
        className="block h-[30px] rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
        onClick={() => getCustomerPrefData()}
      >
        {Texts.customerPrefrenceTitle}
      </DialogTrigger>
      <DialogContent className="custom-scrollbar max-h-[90%] max-w-[650px] overflow-y-auto bg-white sm:rounded-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            <div className="text-2xl font-bold">{Texts.customerPrefrenceTitle}</div>
          </DialogTitle>
        </DialogHeader>
        {loading && <Loader />}
        <Tabs
          defaultValue={DIVISION_LEVEL}
          onValueChange={(value) => setCurrentEditType(value)}
          value={currentEditType}
        >
          <section className="mb-4 pt-3">
            <TabsList>
              <TabsTrigger value={MASTER_LEVEL} className="mr-5 px-0 pb-1 text-lg font-normal text-[#656668]">
                {Texts.tabMasterLevelButton}
              </TabsTrigger>
              <TabsTrigger value={DIVISION_LEVEL} className="px-0 pb-1 text-lg font-normal text-[#656668]">
                {Texts.tabDivisionLevelButton}
              </TabsTrigger>
            </TabsList>
            <section className="mt-10">
              <TabsContent value={MASTER_LEVEL}>
                <Table columns={TABLE_HEADER} data={masterPreferenceData || []} />
              </TabsContent>
              <TabsContent value={DIVISION_LEVEL}>
                <Table columns={TABLE_HEADER} data={divisionPreferenceData || []} />
              </TabsContent>
            </section>
          </section>
        </Tabs>
        <section className="flex justify-start gap-4">
          <Button className="rounded-md bg-deepBlue p-4 text-white" onClick={() => handleSubmit()}>
            {Texts.updateButton}
          </Button>
          <Button
            className="p-0 underline"
            onClick={() => {
              setOpen(false);
              OnCancel(true);
            }}
            type="reset"
          >
            {Texts.cancelButton}
          </Button>
        </section>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerPrefrenceModal;
